import type { HttpFetcherWithBaseUrl } from '~/shared/http-fetcher';
import type { Translate } from '~/shared/internationalization/translation';
import type { RealtimeEventListenerBuilder } from '~/shared/realtime-event-listener/realtime-event-listener';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

import type { ChatFetchers } from './chat/chat-fetchers';
import { createChatFetchers } from './chat/chat-fetchers';
import type { CompanyProfileFetchers } from './company-profile/company-profile-fetchers';
import { createCompanyProfileFetchers } from './company-profile/company-profile-fetchers';
import type { CompanySettingsFetchers } from './company-settings/company-settings-fetchers';
import { createCompanySettingsFetchers } from './company-settings/company-settings-fetchers';
import type { DocumentsFetchers } from './documents/documents-fetchers';
import { createDocumentsFetchers } from './documents/documents-fetchers';
import type { InternalTrackingFetchers } from './internal-tracking/internal-tracking-fetchers';
import { createInternalTrackingFetchers } from './internal-tracking/internal-tracking-fetchers';
import type { CommentsFetchers } from './job-architecture/job-architecture-comments-fetchers';
import { createCommentsFetchers } from './job-architecture/job-architecture-comments-fetchers';
import type { JobArchitectureFetchers } from './job-architecture/job-architecture-fetchers';
import { createJobArchitectureFetchers } from './job-architecture/job-architecture-fetchers';
import type { NotificationsFetchers } from './notifications/notifications-fetchers';
import { createNotificationsFetchers } from './notifications/notifications-fetchers';
import { createSkillFetchers, type SkillFetchers } from './skill/skill-fetchers';
import type { UserManagementFetchers } from './user-management/user-management-fetchers';
import { createUserManagementFetchers } from './user-management/user-management-fetchers';
import type { WatchesFetchers } from './watches/watches-fetchers';
import { createWatchesFetchers } from './watches/watches-fetchers';

export type ApplicationFetchers = ChatFetchers &
    CommentsFetchers &
    CompanyProfileFetchers &
    CompanySettingsFetchers &
    DocumentsFetchers &
    InternalTrackingFetchers &
    JobArchitectureFetchers &
    NotificationsFetchers &
    SkillFetchers &
    UserManagementFetchers &
    WatchesFetchers;

export type { AssignableReviewersItem, AssignReviewerItem } from './job-architecture/job-architecture-fetchers';

export type ApplicationFetchersDependencies = {
    readonly cobrainerApiFetcher: HttpFetcherWithBaseUrl;
    readonly realtimeApiFetcher: HttpFetcherWithBaseUrl;
    readonly realtimeEventListenerBuilder: RealtimeEventListenerBuilder;
    readonly translate: Translate;
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
    readonly validatingRealtimeApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

export const createApplicationFetchers = (dependencies: ApplicationFetchersDependencies): ApplicationFetchers => {
    return {
        ...createChatFetchers(dependencies),
        ...createCommentsFetchers(dependencies),
        ...createCompanyProfileFetchers(dependencies),
        ...createCompanySettingsFetchers(dependencies),
        ...createDocumentsFetchers(dependencies),
        ...createJobArchitectureFetchers(dependencies),
        ...createNotificationsFetchers(dependencies),
        ...createSkillFetchers(dependencies),
        ...createInternalTrackingFetchers(dependencies),
        ...createUserManagementFetchers(dependencies),
        ...createWatchesFetchers(dependencies),
    };
};
